import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiException } from '@/lib/api/types';
import * as accountApi from './accountApi';
import type {
  AccountFunds,
  AccountSummary,
  ConditionalOrders,
  HoldingGuide,
  OrderPage,
  PnlCurvePoint,
} from './accountTypes';

/**
 * 계좌·보유·체결 조회 훅. (12단계 · 2026-09-20 갱신 주기 변경 — BACKLOG I-76)
 *
 * ✏️ **하루 한 번이던 전제가 깨졌다**
 *   이전 주석은 "동기화가 평일 16:00 하루 한 번이라 자주 읽어봐야 같은 값" 이었다.
 *   그 전제가 두 번 깨졌다 — ① 2026-09-14 KRX 애프터마켓이 16:00~20:00 에
 *   열려 그 뒤 체결을 놓쳤고 ② 미국 세션이 KST 22:30~05:00 이라 미국 체결은
 *   **다음 날 16:00까지 최대 24시간** 안 보였다.
 *
 * 🔴 이제 **화면이 열려 있는 동안 60초마다** 읽는다
 *   서버가 `AccountRefreshService.refreshIfStale` 로 throttle 을 건다 —
 *   탭을 여럿 열어도 토스 호출은 분당 1회이고, 화면을 안 보고 있으면
 *   아예 부르지 않는다. 그래서 프론트는 그냥 주기적으로 읽기만 하면 된다.
 *
 * 🔴 여기서 실시간 시세를 붙이지 않는다 (그대로)
 *   보유 종목의 현재가를 매분 갱신하면 화면이 살아 움직이지만,
 *   평가금액·손익·손익률은 그 시세로 다시 계산해야 맞는 값이 된다.
 *   일부만 갱신하면 화면 안에서 시각이 어긋난 숫자가 섞인다.
 *   스냅샷을 그 시점의 사진으로 두고, 기준일을 함께 보여준다.
 */

/** 60초. 서버의 throttle 과 같은 값이다 — 더 자주 쳐도 서버가 토스를 안 부른다. */
const REFRESH_MS = 60 * 1000;

export function useAccountSummary() {
  return useQuery<AccountSummary, ApiException>({
    queryKey: ['trading', 'account', 'summary'],
    queryFn: accountApi.fetchAccountSummary,
    staleTime: REFRESH_MS,
    /*
      🔴 화면이 앞에 있을 때만 갱신한다
        refetchIntervalInBackground 를 켜지 않는다(기본값 false).
        탭을 열어 두고 자리를 비우면 갱신이 멈추고, 돌아오면 다시 돈다.
        "볼 때만 부른다" 는 서버 쪽 판단과 같은 방향이다.
    */
    refetchInterval: REFRESH_MS,
  });
}

/**
 * 돈 카드 (2026-10-16, docs/impl/150 2 · 3번). 요약과 같은 1분 주기로 읽는다 —
 * 서버는 토스를 부르지 않고 우리 표를 읽으므로(현금) 더 자주 칠 이유도, 덜 칠 이유도 없다.
 */
export function useAccountFunds() {
  return useQuery<AccountFunds, ApiException>({
    queryKey: ['trading', 'account', 'funds'],
    queryFn: accountApi.fetchAccountFunds,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
  });
}

/**
 * 실계좌 누적 손익 곡선 (2026-10-16, docs/impl/150 7번). 점이 하루 한두 번 바뀐다 — 10분.
 */
export function usePnlCurve() {
  return useQuery<{ points: PnlCurvePoint[] }, ApiException>({
    queryKey: ['trading', 'account', 'pnl-curve'],
    queryFn: accountApi.fetchPnlCurve,
    staleTime: 10 * REFRESH_MS,
  });
}

/**
 * 보유 종목 × 시스템 기준선 (2026-10-16, docs/impl/150 5번).
 * 일봉 · 감시 행은 하루에 몇 번 바뀐다 — 1분마다 다시 읽을 이유가 없어 10분으로 둔다.
 */
export function useHoldingGuides() {
  return useQuery<HoldingGuide[], ApiException>({
    queryKey: ['trading', 'account', 'holding-guides'],
    queryFn: accountApi.fetchHoldingGuides,
    staleTime: 10 * REFRESH_MS,
  });
}

/**
 * 걸어 둔 조건주문 (2026-10-16, docs/impl/150 4번). 서버가 1분 동안 들고 있으므로 같은 주기로 읽는다.
 */
export function useConditionalOrders() {
  return useQuery<ConditionalOrders, ApiException>({
    queryKey: ['trading', 'account', 'conditional-orders'],
    queryFn: accountApi.fetchConditionalOrders,
    staleTime: REFRESH_MS,
    refetchInterval: REFRESH_MS,
  });
}

/**
 * [지금 동기화] 버튼 (2026-10-16, docs/impl/150 1번).
 *
 * 서버가 돌려준 요약으로 바로 바꿔 끼운다(한 번 더 부르지 않는다). 체결 이력은 서버가 같은 동기화에서 받았으므로
 * 목록 캐시만 낡았다고 표시해 다시 읽게 한다.
 */
export function useRefreshAccount() {
  const queryClient = useQueryClient();
  return useMutation<AccountSummary, ApiException, void>({
    mutationFn: accountApi.refreshAccount,
    onSuccess: (summary) => {
      queryClient.setQueryData(['trading', 'account', 'summary'], summary);
      void queryClient.invalidateQueries({ queryKey: ['trading', 'account', 'orders'] });
      void queryClient.invalidateQueries({ queryKey: ['trading', 'account', 'funds'] });
    },
  });
}

export function useOrders(page: number, size: number) {
  return useQuery<OrderPage, ApiException>({
    queryKey: ['trading', 'account', 'orders', page, size],
    queryFn: () => accountApi.fetchOrders(page, size),
    /*
      체결 이력은 요약(useAccountSummary)만큼 자주 볼 필요가 없다.
      요약을 읽을 때 서버가 동기화하므로 이 목록도 그때 최신이 된다 -
      여기서 또 폴링하면 같은 일을 두 번 시키는 셈이다.
    */
    staleTime: REFRESH_MS,
    // 페이지를 넘길 때 목록이 사라졌다 나타나지 않게 이전 데이터를 유지한다.
    placeholderData: (previous) => previous,
  });
}
